import * as _ from 'lodash-es';
import {BoxDimensions, Dimension, END, otherDimension, Pair, Point, START, X, Y} from '../abstract-units';
import {Cell} from '../cell';
import {CellAreaDefinition} from '../docking/area-definition';
import {EMPTY_CELL_AREA_DEF, SIDE_CELL_AREAS_DEF} from '../docking/area-definitions';
import {CellArea} from '../docking/cell-area';
import {CellAreaType} from '../docking/cell-area-type';
import {
    AXIS_BY_BOX_EDGE,
    BOX_EDGES_BY_AXIS,
    BOX_EDGES_MAP,
    BOX_SIDES_MAP,
    BoxEdge,
    BoxEdges,
    Edge,
    getNewEdgeId,
    OPPOSITE_BOX_EDGES_MAP,
    otherSide,
    Side
} from '../edge';
import {Layout} from '../layout';
import {LayoutChanges} from '../layout-changes';
import {CellActionHistory} from '../layout-history/cell-action-history';
import {EdgeWalker} from '../layout-walkers';
import {MIN_CELL_SIZE, WIDGET_TO_CELL_SIZE_DELTA} from '../layout.constants';
import {OneDimensionSizesStandard, SizesStandard} from '../sizes-standard';
import {CellActionType} from './cell-action-type';

export class CellActions {

    constructor(
        public layout: Layout
    ) {}

    public removeCell(cell: Cell): LayoutChanges {
        this.layout.history.add(new CellActionHistory(cell, CellActionType.CELL_DELETE));

        const changes: LayoutChanges = new LayoutChanges();

        this.excludeCellFromLayout(cell)
            .appendTo(changes);

        // Note: Physical removing of the cells and widgets will be performed by the commit action only.

        changes.add(
            'Cell removed',
            {cell},
            {actionType: CellActionType.CELL_DELETE}
        );

        return changes;
    }

    public insertCell(cell: Cell, targetArea?: CellArea): LayoutChanges {
        targetArea = targetArea || this.findPlaceForCell();

        const changes: LayoutChanges = this.moveCell(cell, targetArea);

        this.layout.registerCell(cell);

        return changes;
    }

    public moveCell(cell: Cell, targetArea: CellArea): LayoutChanges {
        this.layout.history.add(new CellActionHistory(cell, CellActionType.CELL_CHANGE, targetArea));

        const changes: LayoutChanges = new LayoutChanges();

        switch (targetArea.type) {
            case CellAreaType.EMPTY:
                this.placeCellToEmptyArea(cell)
                    .appendTo(changes);
                break;

            case CellAreaType.CENTER:
                this.swapCells(cell, targetArea.cell)
                    .appendTo(changes);
                break;

            case CellAreaType.SIDE:
                this.dockCellToAnotherCell(cell, targetArea.cell, targetArea.boxEdge)
                    .appendTo(changes);
                break;

            case CellAreaType.EDGE:
                const edge: Edge = targetArea.cell.edges[targetArea.boxEdge];
                this.dockCellToEdge(cell, edge)
                    .appendTo(changes);
                break;

            default:
                changes.add('Cell not moved', {cell});
                break;
        }

        return changes;
    }

    public isEnoughSpaceInAreaForCell(cell: Cell, targetArea: CellArea): boolean {
        let isEnoughSpace: boolean = true;
        let spaceSize: Point;

        switch (targetArea.type) {
            case CellAreaType.SIDE:
                spaceSize = this.getPlaceForCellOnCellSide(cell, targetArea.cell, targetArea.boxEdge).size;
                break;

            case CellAreaType.EDGE:
                spaceSize = this.getPlaceForCellAlongEdge(cell, targetArea.cell.edges[targetArea.boxEdge]).size;
                break;
        }

        if (spaceSize) {
            isEnoughSpace = ![Dimension.X, Dimension.Y].some(axis =>
                this.layout.calcDisplayDimension(spaceSize[axis], axis) <
                this.getCellOneDimensionSizesStandard(cell, axis).min
            );
        }

        return isEnoughSpace;
    }

    public isCellOutOfLayout(cell: Cell): boolean {
        return !cell.edges.find(edge => !!edge);
    }

    public getCellOneDimensionSizesStandard(cell: Cell, dimension: Dimension): OneDimensionSizesStandard {
        const getMaxValue = (currentValue: number, candidateValue: number): number =>
            currentValue && candidateValue
                ? Math.max(currentValue, candidateValue)
                : currentValue || candidateValue;

        const maxWidgetSizes = cell.widgets.reduce<OneDimensionSizesStandard>((currentSizes, widget) => {
            const widgetSizes: SizesStandard = widget.getWidgetSizesStandard() || {};
            const widgetMinSize = widgetSizes.min && widgetSizes.min[dimension];
            const widgetDefaultSize = widgetSizes.default && widgetSizes.default[dimension];

            currentSizes.min     = getMaxValue(currentSizes.min, widgetMinSize);
            currentSizes.default = getMaxValue(currentSizes.default, widgetDefaultSize);

            return currentSizes;
        }, {});

        const minCellSize = MIN_CELL_SIZE[dimension];
        const widgetToCellSizeDelta = WIDGET_TO_CELL_SIZE_DELTA[dimension];

        return {
            min:     maxWidgetSizes.min
                         ? getMaxValue(minCellSize, maxWidgetSizes.min + widgetToCellSizeDelta)
                         : minCellSize,
            default: maxWidgetSizes.default
                         && getMaxValue(minCellSize, maxWidgetSizes.default + widgetToCellSizeDelta)
        };
    }

    private excludeCellFromLayout(cell: Cell): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();
        const layoutEdges: Edge[] = this.layout.edges;

        if (this.isCellOutOfLayout(cell)) {
            return changes;
        }

        cell.edges.some((edge: Edge, boxEdge: BoxEdge) => {
            const side: Side = BOX_SIDES_MAP[boxEdge];
            const sideCells: Cell[] = edge.sides[side];
            const canRemoveEdge: boolean = !edge.isBoundary
                && sideCells.length === 1 && sideCells[0] === cell;

            if (canRemoveEdge) {
                const oppositeBoxEdge: BoxEdge = OPPOSITE_BOX_EDGES_MAP[boxEdge];
                const oppositeSide: Side = otherSide(side);
                const oppositeEdge: Edge = cell.edges[oppositeBoxEdge];
                const cellsToUpdate: Cell[] = edge.sides[oppositeSide];

                this.layout.edgeActions.correctEndingCrossEdges(edge, true);

                this.setCellsEdge(cellsToUpdate, oppositeEdge, oppositeBoxEdge);
                this.unbindCellFromEdge(cell, oppositeBoxEdge);

                this.layout.edgeActions.correctAdjoiningEdges(oppositeEdge);

                this.layout.updateCellsDisplayInfo(cellsToUpdate);

                _.remove(layoutEdges, edge);

                changes.add(
                    'Edge removed',
                    {edge, cells: cellsToUpdate},
                    {actionType: CellActionType.CELL_CHANGE}
                );
            }

            return canRemoveEdge; // The only one edge can be removed.
        });

        this.unbindCellFromEdges(cell);

        return changes;
    }

    private placeCellToEmptyArea(cell: Cell): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();

        cell.edges = this.layout.edges.slice() as BoxEdges; // Here we have only four (boundary) edges in the layout.

        this.bindCellToEdges(cell);
        this.layout.updateCellsDisplayInfo([cell]);

        changes.add(
            'Cell placed to empty area',
            {cell},
            {actionType: CellActionType.CELL_CHANGE}
        );

        return changes;
    }

    private swapCells(cell1: Cell, cell2: Cell): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();
        const cell1Edges: Edge[] = cell1.edges;
        const cell2Edges: Edge[] = cell2.edges;

        this.unbindCellFromEdges(cell1);
        this.unbindCellFromEdges(cell2);
        cell1.edges = cell2Edges.slice() as BoxEdges;
        cell2.edges = cell1Edges.slice() as BoxEdges;
        this.bindCellToEdges(cell1);
        this.bindCellToEdges(cell2);

        this.layout.updateCellsDisplayInfo([cell1, cell2]);

        changes.add(
            'Cells swapped',
            {cells: [cell1, cell2]},
            {actionType: CellActionType.CELL_CHANGE}
        );

        return changes;
    }

    private dockCellToAnotherCell(cell: Cell, targetCell: Cell, boxEdge: BoxEdge): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();

        if (cell === targetCell) {
            changes.add('Cell moved to its own place', {cell});

            return changes;
        }

        this.excludeCellFromLayout(cell)
            .appendTo(changes);

        const side: Side = BOX_SIDES_MAP[boxEdge];
        const axis: Dimension = AXIS_BY_BOX_EDGE[boxEdge];
        const movingAxis: Dimension = otherDimension(axis);
        const oppositeBoxEdge: BoxEdge = OPPOSITE_BOX_EDGES_MAP[boxEdge];

        const newCellPlace: BoxDimensions = this.getPlaceForCellOnCellSide(cell, targetCell, boxEdge);
        const newEdgeMovingAxisPos: number = side === Side.FIRST
            ? newCellPlace.pos[movingAxis] + newCellPlace.size[movingAxis]
            : newCellPlace.pos[movingAxis];

        const newEdge: Edge = {
            id:            getNewEdgeId(),
            axis,
            axisRange:     [
                newCellPlace.pos[axis],
                newCellPlace.pos[axis] + newCellPlace.size[axis]
            ],
            movingAxis,
            movingAxisPos: newEdgeMovingAxisPos,
            sides:         [[], []], // To be populated below.
            isBoundary:    false,
            display:       null
        };
        this.layout.edges.push(newEdge);

        cell.edges = targetCell.edges.slice() as BoxEdges;
        cell.edges[oppositeBoxEdge] = newEdge;

        this.unbindCellFromEdge(targetCell, boxEdge);

        targetCell.edges[boxEdge] = newEdge;

        this.bindCellToEdges(cell);
        this.bindCellToEdges(targetCell);

        this.layout.edgeActions.correctEdgeRelatedEdges(newEdge)
            .appendTo(changes);

        this.layout.updateEdgesDisplayInfo([newEdge]);
        this.layout.updateCellsDisplayInfo([cell, targetCell]);

        changes.add(
            'Cell moved',
            {cells: [cell, targetCell], edge: newEdge},
            {actionType: CellActionType.CELL_CHANGE}
        );

        return changes;
    }

    private dockCellToEdge(cell: Cell, edge: Edge): LayoutChanges {
        const changes: LayoutChanges = new LayoutChanges();

        if (this.isCellOwnEdge(cell, edge)) {

            // There is nothing to do when the cell already takes whole the edge.
            changes.add('Cell not moved', {cell});
        } else {
            this.excludeCellFromLayout(cell)
                .appendTo(changes);

            const axis: Dimension = edge.axis;
            const crossAxis: Dimension = edge.movingAxis;

            /**
             * Determines where the new edge will be placed relative to the existing one:
             * next to the existing edge (Side.FIRST) or before it (Side.SECOND).
             * @type {Side}
             */
            const newEdgeSide: Side = edge.isBoundary && !edge.sides[Side.FIRST].length
                ? Side.SECOND
                : Side.FIRST;
            const existingEdgeSide: Side = otherSide(newEdgeSide);

            // Calculate size and position for the cell.
            const newCellPlace: BoxDimensions = this.getPlaceForCellAlongEdge(cell, edge);
            const sidedEdgePositions: Point = [
                newCellPlace.pos[crossAxis] + newCellPlace.size[crossAxis],
                newCellPlace.pos[crossAxis]
            ];

            const newEdgeSideEndingEdges: Pair<Edge> = new EdgeWalker(edge)
                .sidedEndingCrossEdges()
                .sidedEdges[newEdgeSide] as Pair<Edge>;

            const newEdge: Edge = {
                id:            getNewEdgeId(),
                axis,
                axisRange:     edge.axisRange.slice() as Point,
                movingAxis:    crossAxis,
                movingAxisPos: sidedEdgePositions[newEdgeSide],
                sides:         [[], []], // To be populated below.
                isBoundary:    false,
                display:       null
            };
            this.layout.edges.push(newEdge);

            edge.movingAxisPos = sidedEdgePositions[existingEdgeSide];

            this.setCellsEdge(edge.sides[newEdgeSide], newEdge, BOX_EDGES_MAP[axis][newEdgeSide]);
            edge.sides[newEdgeSide] = []; // To be populated with the moving cell below.

            // Bind four edges to the cell.
            this.setCellEdge(cell, newEdge, BOX_EDGES_MAP[axis][existingEdgeSide]);
            this.setCellEdge(cell, edge, BOX_EDGES_MAP[axis][newEdgeSide]);
            this.setCellEdge(cell, newEdgeSideEndingEdges[START], BOX_EDGES_MAP[crossAxis][Side.FIRST]);
            this.setCellEdge(cell, newEdgeSideEndingEdges[END], BOX_EDGES_MAP[crossAxis][Side.SECOND]);

            this.layout.edgeActions.correctEdgeRelatedEdges(edge)
                .appendTo(changes);
            this.layout.edgeActions.correctEdgeRelatedEdges(newEdge)
                .appendTo(changes);

            const edges: Edge[] = [edge, newEdge];
            const cells: Cell[] = this.layout.getEdgesCells(edges);

            this.layout.updateEdgesDisplayInfo(edges);
            this.layout.updateCellsDisplayInfo(cells);

            changes.add(
                'Cell moved',
                {cells, edges},
                {actionType: CellActionType.CELL_CHANGE}
            );
        }

        return changes;
    }

    private unbindCellFromEdges(cell: Cell): void {
        cell.edges.forEach(
            (edge: Edge, boxEdge: BoxEdge) => this.unbindCellFromEdge(cell, boxEdge)
        );
    }

    private unbindCellFromEdge(cell: Cell, boxEdge: BoxEdge): void {
        const edge: Edge = cell.edges[boxEdge];

        if (edge) {
            const side: Side = BOX_SIDES_MAP[boxEdge];
            const sideCells: Cell[] = edge.sides[side];
            const iCell: number = sideCells.indexOf(cell);

            if (iCell >= 0) {
                sideCells.splice(iCell, 1);
            }

            cell.edges[boxEdge] = null;
        }
    }

    private bindCellToEdges(cell: Cell): void {
        cell.edges.forEach(
            (edge: Edge, boxEdge: BoxEdge) => this.bindCellToEdge(cell, boxEdge)
        );
    }

    private bindCellToEdge(cell: Cell, boxEdge: BoxEdge): void {
        const edge: Edge = cell.edges[boxEdge];
        const side: Side = BOX_SIDES_MAP[boxEdge];
        const sideCells: Cell[] = edge.sides[side];
        const iCell: number = sideCells.indexOf(cell);

        if (iCell < 0) {
            sideCells.push(cell);
            this.layout.sortEdgeSideCells(edge, side);
        }
    }

    private setCellEdge(cell: Cell, edge: Edge, boxEdge: BoxEdge): void {
        cell.edges[boxEdge] = edge;

        this.bindCellToEdge(cell, boxEdge);
    }

    private setCellsEdge(cells: Cell[], edge: Edge, boxEdge: BoxEdge): void {
        cells.forEach(cell => this.setCellEdge(cell, edge, boxEdge));
    }

    /**
     * Finds a space for a new cell by splitting an existing cell into two parts.
     * @param {Cell} cell
     * @param {Cell} hostCell
     * @param {BoxEdge} boxEdge
     * @returns {BoxDimensions} position and size for the new cell.
     */
    private getPlaceForCellOnCellSide(cell: Cell, hostCell: Cell, boxEdge: BoxEdge): BoxDimensions {
        const edge: Edge = hostCell.edges[boxEdge];
        const axis: Dimension = edge.axis;
        const movingAxis: Dimension = edge.movingAxis;
        const edgeSide: Side = BOX_SIDES_MAP[boxEdge];

        const parallelBoxEdges: Pair<BoxEdge> = BOX_EDGES_BY_AXIS[axis];
        const crossBoxEdges: Pair<BoxEdge> = BOX_EDGES_BY_AXIS[movingAxis];
        const parallelEdges: Pair<Edge> = [
            hostCell.edges[parallelBoxEdges[START]],
            hostCell.edges[parallelBoxEdges[END]]
        ];
        const crossEdges: Pair<Edge> = [
            hostCell.edges[crossBoxEdges[START]],
            hostCell.edges[crossBoxEdges[END]]
        ];

        // Set normal dimensions to be equal to display ones. It's required for operations with cell's SizesStandard.
        this.layout.setLayoutSize(this.layout.displaySize);

        const size: Point = [0, 0];

        size[movingAxis] = this.shareDimensionBetweenTwoCells(
            cell,
            hostCell,
            movingAxis,
            parallelEdges[END].movingAxisPos - parallelEdges[START].movingAxisPos,
            edgeSide
        )[edgeSide];

        size[axis] = crossEdges[END].movingAxisPos - crossEdges[START].movingAxisPos;

        const pos: Point = [0, 0];
        pos[movingAxis] = edgeSide === Side.FIRST
            ? parallelEdges[START].movingAxisPos
            : parallelEdges[END].movingAxisPos - size[movingAxis];
        pos[axis] = crossEdges[START].movingAxisPos;

        return {pos, size};
    }

    /**
     * Finds a space for a new cell in case of placing the cell on an edge, between two sides of existing cells.
     * @param {Cell} cell
     * @param {Edge} targetEdge
     * @returns {BoxDimensions} position and size for the new cell.
     */
    private getPlaceForCellAlongEdge(cell: Cell, targetEdge: Edge): BoxDimensions {
        const axis: Dimension = targetEdge.axis;
        const movingAxis: Dimension = targetEdge.movingAxis;

        // Set normal dimensions to be equal to display ones. It's required for operations with cell's SizesStandard.
        this.layout.setLayoutSize(this.layout.displaySize);

        const [movingAxisPos, movingAxisSize] = this.findAvailableCellSizeByEdge(cell, targetEdge);

        const pos: Point = [0, 0];
        pos[movingAxis] = movingAxisPos;
        pos[axis] = targetEdge.axisRange[START];

        const size: Point = [0, 0];
        size[movingAxis] = movingAxisSize;
        size[axis] = targetEdge.axisRange[END] - targetEdge.axisRange[START];

        return {pos, size};
    }

    /**
     * Finds a space for a new cell in case of placing the cell on an edge, between two sides of existing cells.
     * @param {Cell} cell
     * @param {Edge} edge
     * @returns {Pair<number>} one-dimensional position and size for the new cell (on the moving-axis).
     */
    private findAvailableCellSizeByEdge(cell: Cell, edge: Edge): Pair<number> {
        const edgePos: number = edge.movingAxisPos;
        const edgeAxis: Dimension = edge.axis;

        const cellSizesStandard = this.getCellOneDimensionSizesStandard(cell, edge.movingAxis);

        const sidedReductions: Pair<OneDimensionSizesStandard> = [{}, {}]; // Filled below in a cycle.
        const sidedCellSizes: Pair<number[]> = [[], []]; // Filled below in a cycle.

        const getMinOrNew = (oldValue: number, newValue: number) =>
            _.isNil(oldValue)
                ? newValue
                : Math.min(oldValue, newValue);

        edge.sides.forEach((sideCells: Cell[], side: Side) => {
            const boxEdge: BoxEdge = BOX_EDGES_MAP[edgeAxis][side];
            const oppositeBoxEdge: BoxEdge = OPPOSITE_BOX_EDGES_MAP[boxEdge];

            sideCells.forEach(sideCell => {
                const sideCellOppositeEdge = sideCell.edges[oppositeBoxEdge];
                const size = Math.abs(edgePos - sideCellOppositeEdge.movingAxisPos);
                const sizesStandard = this.getCellOneDimensionSizesStandard(sideCell, edge.movingAxis);
                const allowedReduction: OneDimensionSizesStandard = {
                    min:     Math.max(0, size - (sizesStandard.min || 0)),
                    default: Math.max(0, size - (sizesStandard.default || size))
                };

                sidedReductions[side] = {
                    min:     getMinOrNew(sidedReductions[side].min, allowedReduction.min),
                    default: getMinOrNew(sidedReductions[side].default, allowedReduction.default)
                };

                sidedCellSizes[side].push(size);
            });
        });

        // Ground the reduction values in order to safely do math operations on their.
        sidedReductions.forEach(sideReduction => {
            sideReduction.min = sideReduction.min || 0;
            sideReduction.default = sideReduction.default || 0;
        });

        const sideCount = edge.isBoundary ? 1 : 2;
        const {min: cellMinSize, default: cellDefaultSize} = cellSizesStandard;
        const totalMinReduction = _.sumBy(sidedReductions, sideReduction => sideReduction.default);
        const totalMaxReduction = _.sumBy(sidedReductions, sideReduction => sideReduction.min);

        const getNewEdgePos = (neededSpace: number, sidedReduction: Pair<number>): number => {
            const [rightSpace, leftSpace] = sidedReduction;
            const kLeftSpace = leftSpace / ((rightSpace + leftSpace) || 1);
            const leftShift = Math.round(neededSpace * kLeftSpace);

            return edgePos - leftShift;
        };
        const sidedMaxReduction = sidedReductions.map(sideReduction => sideReduction.min) as Pair<number>;
        const sidedMinReduction = sidedReductions.map(sideReduction => sideReduction.default) as Pair<number>;

        interface PlacementCase {
            condition: () => boolean;
            placement: () => [number, number]; // [pos, size]
        }

        const placementCases: PlacementCase[] = [
            {
                // Is there enough space for cell's default size?
                condition: () => cellDefaultSize && cellDefaultSize <= totalMaxReduction,

                // Set new cell to its default size.
                placement: () => [
                    getNewEdgePos(cellDefaultSize, sidedMaxReduction),
                    cellDefaultSize
                ]
            },
            {
                // Is minimal reduction is enough for cell's minimal size?
                condition: () => cellMinSize && cellMinSize <= totalMinReduction,

                // Set existed cells to default size. Give the rest of space to the new cell.
                placement: () => [
                    getNewEdgePos(totalMinReduction, sidedMinReduction),
                    totalMinReduction
                ]
            },
            {
                // Does only cell's minimal size or less fit?
                condition: () => cellMinSize >= totalMaxReduction,

                // Set new cell to maximal allowed size.
                placement: () => [
                    getNewEdgePos(totalMaxReduction, sidedMaxReduction),
                    totalMaxReduction
                ]
            },
            {
                // Default placement.
                condition: () => true,

                // Calculate an adaptive size for the new cell.
                placement: () => {
                    const [rightSpace, leftSpace] = sidedMaxReduction;

                    const [smallestRightSize, smallestLeftSize] = sidedCellSizes.map(sideCellSizes =>
                        sideCellSizes.length
                            ? Math.min(...sideCellSizes)
                            : 0
                    );
                    const adaptiveNewCellSize = leftSpace === rightSpace
                        ? Math.round(Math.min(smallestRightSize, smallestLeftSize) * sideCount / (sideCount + 1))
                        : rightSpace > leftSpace
                            ? Math.round((smallestRightSize + leftSpace * leftSpace / totalMaxReduction) / 2)
                            : Math.round((smallestLeftSize + rightSpace * rightSpace / totalMaxReduction) / 2);

                    const newCellSize = Math.min(adaptiveNewCellSize, totalMaxReduction);

                    return [
                        getNewEdgePos(newCellSize, sidedMaxReduction),
                        newCellSize
                    ];
                }
            }
        ];

        const placementCase = placementCases.find(currentPlacementCase => currentPlacementCase.condition());

        return placementCase.placement();
    }

    /**
     * Suggests to split the largest cell in order to put a new one.
     * Used in case of default placing only.
     * @returns {CellArea}
     */
    private findPlaceForCell(): CellArea {
        interface CellSizeMeta {
            cell: Cell;
            square: number;
        }

        const largestCellMeta: CellSizeMeta = this.layout.cells.reduce<CellSizeMeta>(
            (cellMeta: CellSizeMeta, currentCell: Cell) => {
                const square: number = currentCell.display.size[X] * currentCell.display.size[Y];

                return cellMeta && cellMeta.square >= square
                    ? cellMeta
                    : {
                        cell: currentCell,
                        square
                    };
            },
            null
        );
        const largestCell: Cell = largestCellMeta && largestCellMeta.cell;
        const areaDef: CellAreaDefinition = largestCell
            ? largestCell.display.size[X] > largestCell.display.size[Y]
                ? SIDE_CELL_AREAS_DEF[BoxEdge.RIGHT]
                : SIDE_CELL_AREAS_DEF[BoxEdge.BOTTOM]
            : EMPTY_CELL_AREA_DEF;

        return new CellArea(areaDef, largestCell);
    }

    private isCellOwnEdge(cell: Cell, edge: Edge): boolean {
        return !!edge.sides
            .find(cells => cells.length === 1 && cells[0] === cell);
    }

    /**
     * Finds a space for a new cell by splitting an existing cell into two parts.
     * @param {Cell} cell
     * @param {Cell} hostCell
     * @param {Dimension} dimension
     * @param {number} totalSpace
     * @param {Side} edgeSide
     * @returns {Pair<number>} one-dimensional sizes for the both cells (on the moving-axis).
     */
    // tslint:disable cyclomatic-complexity
    private shareDimensionBetweenTwoCells(
        cell: Cell,
        hostCell: Cell,
        dimension: Dimension,
        totalSpace: number,
        edgeSide: Side
    ): Pair<number> {
        const cellSizes = this.getCellOneDimensionSizesStandard(cell, dimension);
        const hostCellSizes = this.getCellOneDimensionSizesStandard(hostCell, dimension);
        const oppositeEdgeSide = otherSide(edgeSide);

        /**
         * cMin / cDef - stands for "Cell" min/default size;
         * hMin / hDef - stands for "Host cell" min/default size;
         * aDef / bDef - default sizes for cell on the First and Second sides correspondingly.
         */
        const {default: cDef, min: cMin} = cellSizes;
        const {default: hDef, min: hMin} = hostCellSizes;
        const [aDef, bDef] = edgeSide === Side.FIRST ? [cDef, hDef] : [hDef, cDef];

        const sizes: Point = [0, 0]; // To be updated below.

        switch (true) {

            // Set top-left cell to default size, if both have defaults.
            case (aDef && bDef && (aDef + bDef <= totalSpace)):
                sizes[Side.FIRST] = aDef;
                sizes[Side.SECOND] = totalSpace - aDef;

                break;

            // Set existed cell to default size.
            case (cMin && hDef && (cMin + hDef <= totalSpace)):
                sizes[oppositeEdgeSide] = hDef;
                sizes[edgeSide] = totalSpace - sizes[oppositeEdgeSide];

                break;

            // Set new cell to default size.
            case (cDef && hMin && (cDef + hMin <= totalSpace)):
                sizes[edgeSide] = cDef;
                sizes[oppositeEdgeSide] = totalSpace - cDef;

                break;

            // Split proportionally to their minimums.
            case !!(cMin && hMin):
                sizes[edgeSide] = Math.round(cMin * totalSpace / (cMin + hMin));
                sizes[oppositeEdgeSide] = totalSpace - sizes[edgeSide];

                break;
        }

        return sizes;
    }

}
